/**
 * Pure conditional-formatting evaluator. Mirrors the OR-of-AND grammar
 * used by `signal_engine` in the backtester: a rule's `groups` is
 * `Condition[][]` where the outer array is OR and the inner array is
 * AND. First-match-wins across rules. No React, no async — feed it a
 * rule list and a numeric value, get back the active style.
 */

export type CfOp = '>' | '>=' | '<' | '<=' | '==' | '!=';

export const CF_OPS: readonly CfOp[] = ['>', '>=', '<', '<=', '==', '!='];

export interface CfCondition {
  op: CfOp;
  target: number;
}

export interface CfRule {
  id: string;
  groups: CfCondition[][];
  textColor?: string;
  bgColor?: string;
}

export interface CfActiveStyle {
  ruleId: string;
  textColor?: string;
  bgColor?: string;
}

export const MAX_CF_RULES_PER_CELL = 16;
export const MAX_CF_OR_GROUPS_PER_RULE = 8;
export const MAX_CF_AND_CONDITIONS_PER_GROUP = 8;
export const MAX_CF_RULE_ID_LEN = 64;

const EPS_BASE = 1e-9;

const approxEqual = (a: number, b: number): boolean => {
  if (a === b) return true;
  const tolerance = EPS_BASE * Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) <= tolerance;
};

const evalCondition = (cond: CfCondition, value: number): boolean => {
  if (!Number.isFinite(cond.target)) return false;
  switch (cond.op) {
    case '>':
      return value > cond.target && !approxEqual(value, cond.target);
    case '>=':
      return value > cond.target || approxEqual(value, cond.target);
    case '<':
      return value < cond.target && !approxEqual(value, cond.target);
    case '<=':
      return value < cond.target || approxEqual(value, cond.target);
    case '==':
      return approxEqual(value, cond.target);
    case '!=':
      return !approxEqual(value, cond.target);
    default:
      return false;
  }
};

const groupFires = (group: CfCondition[], value: number): boolean => {
  if (group.length === 0) return false;
  return group.every((cond) => evalCondition(cond, value));
};

const ruleFires = (rule: CfRule, value: number): boolean => {
  if (rule.groups.length === 0) return false;
  return rule.groups.some((group) => groupFires(group, value));
};

/**
 * First matching rule wins. Returns `null` when `value` is null /
 * non-finite, when there are no rules, or when no rule's groups fire.
 * Rule colors are passed through as-is — `null` matches when one or
 * both colors are absent.
 */
export function evaluateRules(
  rules: readonly CfRule[],
  value: number | null,
): CfActiveStyle | null {
  if (value === null || !Number.isFinite(value)) return null;
  for (const rule of rules) {
    if (ruleFires(rule, value)) {
      return {
        ruleId: rule.id,
        textColor: rule.textColor,
        bgColor: rule.bgColor,
      };
    }
  }
  return null;
}

interface MemoSlot {
  value: number;
  result: CfActiveStyle | null;
}

const lastEvalCache: WeakMap<readonly CfRule[], MemoSlot> = new WeakMap();

/**
 * Reference-keyed memoised wrapper around `evaluateRules`. Skips
 * re-evaluation when called again with the same rules array reference
 * and the same numeric value — the common case across re-renders that
 * are unrelated to a cell's rules / value (selection change, edit,
 * etc.). The cache holds one slot per rules array; entries are
 * GC'd via WeakMap when the rules array is replaced.
 */
export function memoEvaluateRules(
  rules: readonly CfRule[],
  value: number | null,
): CfActiveStyle | null {
  if (rules.length === 0) return null;
  if (value === null || !Number.isFinite(value)) return null;
  const hit = lastEvalCache.get(rules);
  if (hit && Object.is(hit.value, value)) return hit.result;
  const result = evaluateRules(rules, value);
  lastEvalCache.set(rules, { value, result });
  return result;
}

/**
 * Type guard. Used by `dashboardsApi` decode and `isExcelConfig` shape
 * validation in Phase 3 — defined here so the evaluator owns the rule
 * shape.
 */
export function isCfRule(value: unknown): value is CfRule {
  if (!value || typeof value !== 'object') return false;
  const r = value as Partial<CfRule>;
  if (typeof r.id !== 'string') return false;
  if (!Array.isArray(r.groups)) return false;
  for (const group of r.groups) {
    if (!Array.isArray(group)) return false;
    for (const cond of group) {
      if (!cond || typeof cond !== 'object') return false;
      const c = cond as Partial<CfCondition>;
      if (typeof c.op !== 'string' || !CF_OPS.includes(c.op as CfOp)) return false;
      if (typeof c.target !== 'number' || !Number.isFinite(c.target)) return false;
    }
  }
  if (r.textColor !== undefined && typeof r.textColor !== 'string') return false;
  if (r.bgColor !== undefined && typeof r.bgColor !== 'string') return false;
  return true;
}
